import { useEffect, useState } from "react";

import Modal from "../../layouts/Modal";
import { ILead, LeadStatus } from "../../models/Lead";
import useSingleLead from "../../data/useSingleLead";
import useNotification from "../notifications/useNotifications";

export const cancellationReasons = [
  "Sold elsewhere",
  "Customer changed their mind",
  "Price not accepted",
  "Couldn't contact customer",
  "Duplicate lead",
  "Vehicle not as described",
  "Other",
];

interface CancelLeadModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  lead: ILead | null;
  onCancelled?: () => void;
}

// Moves a lead to Cancelled with a reason. The API stamps the date, and clears all of it if the
// lead is later moved back to another status.
export default function CancelLeadModal({ open, setOpen, lead, onCancelled }: CancelLeadModalProps) {
  const { update } = useSingleLead(lead?.id ?? null);
  const { addNotification } = useNotification();
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setNote("");
    }
  }, [open]);

  const noteRequired = reason === "Other";
  const isValid = !!reason && (!noteRequired || note.trim().length > 0);

  const handleConfirm = () => {
    if (!lead || !isValid) return;

    update.mutate(
      {
        ...lead,
        status: LeadStatus["Cancelled"],
        cancellationReason: reason,
        cancellationNote: note.trim() || null,
      },
      {
        onSuccess: () => {
          addNotification({ variant: "success", primaryText: "Lead cancelled" });
          onCancelled?.();
          setOpen(false);
        },
        onError: () => {
          addNotification({ variant: "error", primaryText: "Unable to cancel lead" });
        },
      }
    );
  };

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-md">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}
      >
        <h3 className="text-lg font-medium leading-6 text-gray-900">Cancel this lead?</h3>

        <div className="flex flex-col gap-1">
          <label htmlFor="cancelReason" className="text-sm font-medium text-gray-700">
            Reason
          </label>
          <select
            id="cancelReason"
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-md border border-gray-300 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">Choose a reason</option>
            {cancellationReasons.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="cancelNote" className="text-sm font-medium text-gray-700">
            Details <span className="font-normal text-gray-500">{noteRequired ? "(required for Other)" : "(optional)"}</span>
          </label>
          <textarea
            id="cancelNote"
            rows={3}
            maxLength={500}
            required={noteRequired}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What happened"
            className="w-full rounded-md border border-gray-300 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <p className="text-xs text-gray-600">You can reopen the lead later by changing its status.</p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            Keep lead
          </button>
          <button
            type="submit"
            disabled={!isValid || update.isLoading}
            className="rounded-md border border-red-700 bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-60"
          >
            {update.isLoading ? "Cancelling…" : "Cancel lead"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
